import type { HintReference, RepNote } from "@scoach/types";
import { Close, Doc, Image, Notebook, Question, Send, Spark } from "@scoach/ui/icons";
import { type ReactNode, useRef, useState } from "react";

import { effectiveGeminiKey, ensureGeminiKey } from "../../../../lib/geminiKey.ts";
import { api, streamSse } from "../../../../lib/http.ts";
import { safeHref } from "../../../../lib/safeHref.ts";
import { useAuthStore } from "../../../auth/store.ts";
import { type AskDepth, streamQuietAsk } from "../gemini/quietAsk.ts";
import { useLiveMeetingStore } from "../store.ts";

export interface QuietBarProps {
  meetingId: string;
  height?: number;
  onResizePreset?: (preset: "compact" | "standard" | "expanded") => void;
}

interface QuietAnswer {
  q: string;
  /** Markdown answer text, accumulated as it streams in. */
  a: string;
  chips?: string[];
  references?: HintReference[];
  /** Which control produced this answer — drives the accent color. */
  kind?: "ask" | "urgent";
  /** The depth this answer was generated at (drives the "get a deeper answer"
   * offer after short answers, and the inline infographic after deep ones). */
  askedDepth?: AskDepth;
}

const RTL_RE = /[֐-׿؀-ۿﭐ-﷿ﹰ-﻿]/;
function isRtl(text: string): boolean {
  return RTL_RE.test(text);
}

const BULLET_RE = /^(?:[-*•]\s+|\d+[.)]\s+)(.*)$/;

/** Render inline `**bold**` markdown; everything else is plain text. */
function renderInline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  // biome-ignore lint/suspicious/noAssignInExpressions: standard regex exec loop
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(<strong key={`${keyBase}-b${i++}`}>{m[1]}</strong>);
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/**
 * Render the streamed light-markdown answer as a glanceable card: a bold lead
 * line + dash bullets with accent dots. Tolerates partial markdown (mid-stream).
 */
function AnswerBody({ text }: { text: string }) {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  let headline: string | null = null;
  const bullets: string[] = [];
  const paras: string[] = [];
  for (const line of lines) {
    const b = line.match(BULLET_RE);
    if (b) {
      bullets.push(b[1] ?? "");
      continue;
    }
    if (headline === null) {
      // Strip surrounding ** — the headline is already styled bold.
      headline = line.replace(/^\*\*\s*/, "").replace(/\s*\*\*$/, "");
      continue;
    }
    paras.push(line);
  }

  return (
    <>
      {headline && <div className="qa-headline">{renderInline(headline, "h")}</div>}
      {bullets.length > 0 && (
        <ul className="qa-points">
          {bullets.map((p, i) => (
            <li key={`b-${i}-${p.slice(0, 8)}`}>
              <span className="hp-dot" />
              {renderInline(p, `b${i}`)}
            </li>
          ))}
        </ul>
      )}
      {paras.map((p, i) => (
        <p key={`p-${i}-${p.slice(0, 8)}`} className="qa-line">
          {renderInline(p, `p${i}`)}
        </p>
      ))}
    </>
  );
}

export function QuietBar({ meetingId, height, onResizePreset }: QuietBarProps) {
  const notes = useLiveMeetingStore((s) => s.notes);
  const addNote = useLiveMeetingStore((s) => s.addNote);
  const startedAt = useLiveMeetingStore((s) => s.startedAt);

  const [mode, setMode] = useState<"ask" | "note">("ask");
  const [val, setVal] = useState("");
  // Answer depth for Quiet Ask: "short" (default, glanceable) or "deep" (detailed).
  // Deep-dive focus defaults to deep answers (the rep can still switch to short).
  const hintFocus = useLiveMeetingStore((s) => s.hintFocus);
  const [depth, setDepth] = useState<AskDepth>(hintFocus === "deepdive" ? "deep" : "short");
  const [answer, setAnswer] = useState<QuietAnswer | null>(null);
  const [thinking, setThinking] = useState(false);
  const [streaming, setStreaming] = useState(false);
  // Inline infographic generated for a DEEP answer (base64), plus its states.
  const [answerIg, setAnswerIg] = useState<{ imageBase64: string; mimeType: string } | null>(null);
  const [igLoading, setIgLoading] = useState(false);
  const [igExpanded, setIgExpanded] = useState(false);
  // Accumulates streamed deltas without relying on React state for concatenation.
  const accRef = useRef("");

  function clearAnswer() {
    setAnswer(null);
    setAnswerIg(null);
    setIgExpanded(false);
    setIgLoading(false);
  }

  /** After a DEEP answer, generate a relevant infographic image (needs a personal
   * Gemini key) and show it inline at the end of the answer. Best-effort. */
  async function generateAnswerInfographic(topic: string, answerText: string) {
    let key = effectiveGeminiKey();
    if (!key) key = await ensureGeminiKey();
    if (!key || !answerText.trim()) return;
    setAnswerIg(null);
    setIgExpanded(false);
    setIgLoading(true);
    try {
      const r = await api<{ imageBase64?: string; mimeType?: string }>(
        `/meetings/${meetingId}/answer-infographic`,
        { method: "POST", headers: { "x-gemini-key": key }, body: { topic, answer: answerText } },
      ).catch(() => null);
      if (r?.imageBase64)
        setAnswerIg({ imageBase64: r.imageBase64, mimeType: r.mimeType ?? "image/png" });
    } finally {
      setIgLoading(false);
    }
  }

  function elapsed(): string {
    if (!startedAt) return "00:00";
    const sec = Math.floor((Date.now() - startedAt) / 1000);
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  function persistQa(q: string, a: string, isUrgent: boolean) {
    const text = a.trim();
    if (!text) return;
    api(`/meetings/${meetingId}/qa`, {
      method: "POST",
      body: { q, a: text, chips: [], at: new Date().toISOString(), urgent: isUrgent },
    }).catch(() => {});
  }

  function submit() {
    if (!val.trim()) return;
    if (mode === "note") {
      const note: RepNote = { t: elapsed(), text: val.trim() };
      addNote(note);
      void api(`/meetings/${meetingId}/notes`, { method: "POST", body: note }).catch(() => {});
      setVal("");
      return;
    }
    const question = val;
    setVal("");
    void runAsk(question, depth);
  }

  /** Run a Quiet-ask for `question` at the given depth. Reusable so the
   * "Get a deeper answer" button can re-ask the same question at depth "deep". */
  async function runAsk(question: string, askDepth: AskDepth) {
    if (height !== undefined && height <= 110) {
      onResizePreset?.("standard");
    }
    setThinking(true);
    setStreaming(true);
    accRef.current = "";
    setAnswerIg(null);
    setIgExpanded(false);
    setAnswer({ q: question, a: "", kind: "ask", references: [], askedDepth: askDepth });

    const onDelta = (t: string) => {
      accRef.current += t;
      setThinking(false);
      setAnswer((prev) => (prev ? { ...prev, a: accRef.current } : prev));
    };
    const hasKey = !!useAuthStore.getState().geminiKey;

    try {
      try {
        // Primary: server stream grounded in the knowledge base (+ web fallback),
        // with document citations. Works without a personal key.
        await streamSse<HintReference[]>(
          `/meetings/${meetingId}/quiet-ask/stream`,
          { question, depth: askDepth },
          {
            onDelta,
            onReferences: (refs) =>
              setAnswer((prev) => (prev ? { ...prev, references: refs ?? [] } : prev)),
          },
        );
      } catch {
        // Fallbacks: personal-key client stream, else a one-shot server call.
        if (hasKey) {
          await streamQuietAsk(question, onDelta, useAuthStore.getState().user?.role, askDepth);
        } else {
          // No personal key: try the non-stream server route once. If that also
          // fails (e.g. KB not configured), leave the answer empty so the
          // friendly "set up a key" message below is shown instead of an error.
          const r = await api<{ answer: string; references: HintReference[] }>(
            `/meetings/${meetingId}/quiet-ask`,
            { method: "POST", body: { question, depth: askDepth } },
          ).catch(() => null);
          if (r) {
            accRef.current = r.answer ?? "";
            setAnswer((prev) =>
              prev ? { ...prev, a: accRef.current, references: r.references ?? [] } : prev,
            );
          }
        }
      }

      if (!accRef.current.trim()) {
        accRef.current = "No answer found.";
        setAnswer((prev) => (prev ? { ...prev, a: accRef.current } : prev));
      }
      persistQa(question, accRef.current, false);
      // For a DEEP answer, add a relevant infographic at the end (best-effort).
      if (askDepth === "deep" && accRef.current.trim()) {
        void generateAnswerInfographic(question, accRef.current);
      }
    } catch (err) {
      setAnswer({
        q: question,
        a: `Quiet Ask failed: ${(err as Error).message}`,
        kind: "ask",
      });
    } finally {
      setThinking(false);
      setStreaming(false);
    }
  }

  const busy = thinking;
  const showShimmer = busy && !answer?.a;
  const rtl = answer ? isRtl(answer.a || answer.q) : false;

  return (
    <div
      className="quiet-bar"
      style={
        height !== undefined
          ? { height, minHeight: height, maxHeight: height }
          : undefined
      }
    >
      <div className="seg seg-sm quiet-seg">
        <button
          type="button"
          className={mode === "ask" ? "on" : ""}
          onClick={() => {
            setMode("ask");
            clearAnswer();
          }}
        >
          <Question size={14} /> Quiet ask
        </button>
        <button
          type="button"
          className={mode === "note" ? "on" : ""}
          onClick={() => {
            setMode("note");
            clearAnswer();
          }}
        >
          <Notebook size={14} /> Private note
        </button>
      </div>

      <div className="quiet-input-row">
        <textarea
          className="quiet-input-ta"
          rows={2}
          placeholder={
            mode === "ask"
              ? "Ask Gemini privately — answer goes only to you"
              : "Note for yourself — added to context"
          }
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            // Enter submits; Shift+Enter inserts a newline.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <span className="kbd">⌘</span>
        <span className="kbd">↵</span>
        {mode === "ask" && (
          <select
            className="quiet-depth-select"
            value={depth}
            onChange={(e) => setDepth(e.target.value as AskDepth)}
            title="Short = a quick, glanceable answer. Deep = a detailed, in-depth answer."
            style={{ fontSize: 12 }}
          >
            <option value="short">Short answer</option>
            <option value="deep">Deep answer</option>
          </select>
        )}
        <button type="button" className="pill-btn primary sm" onClick={submit}>
          <Send size={14} /> {mode === "ask" ? "Ask" : "Save"}
        </button>
      </div>

      {showShimmer && (
        <div className="quiet-answer thinking">
          <div className="shimmer-line shimmer" style={{ width: "75%" }} />
          <div className="shimmer-line shimmer" style={{ width: "55%" }} />
        </div>
      )}
      {answer?.a && !showShimmer && (
        <div
          className={`quiet-answer ${answer.kind === "urgent" ? "is-urgent" : "is-ask"}`}
          dir={rtl ? "rtl" : "ltr"}
        >
          <div className="qa-q">
            <Question size={14} /> {answer.q}
          </div>
          <div className="qa-body">
            <AnswerBody text={answer.a} />
            {streaming && <span className="qa-caret" aria-hidden="true" />}
          </div>
          {answer.references && answer.references.length > 0 && (
            <div className="hint-refs">
              <span className="hint-refs-label">Sources:</span>
              {answer.references.map((r) => (
                <a
                  key={`${r.docId}-${r.page ?? ""}`}
                  className="ref-chip"
                  href={safeHref(r.url)}
                  target="_blank"
                  rel="noreferrer"
                  title={`Open ${r.docName}${r.page ? ` at page ${r.page}` : ""}`}
                >
                  <Doc size={12} /> {r.docName}
                  {r.page ? ` · p.${r.page}` : ""}
                </a>
              ))}
            </div>
          )}

          {/* After a SHORT answer, offer a deeper one on the same question. */}
          {!streaming && answer.a && answer.kind !== "urgent" && answer.askedDepth === "short" && (
            <button
              type="button"
              className="qa-deeper"
              onClick={() => void runAsk(answer.q, "deep")}
            >
              <Spark size={13} /> Get a deeper answer
            </button>
          )}

          {/* After a DEEP answer, show a relevant infographic at the end. */}
          {answer.askedDepth === "deep" && !streaming && (igLoading || answerIg) && (
            <div className="qa-infographic">
              {igLoading ? (
                <div className="qa-ig-loading">
                  <Image size={14} /> Generating an infographic…
                </div>
              ) : (
                answerIg && (
                  <button
                    type="button"
                    className="qa-ig-thumb"
                    onClick={() => setIgExpanded(true)}
                    title="Click to enlarge"
                  >
                    <img
                      src={`data:${answerIg.mimeType};base64,${answerIg.imageBase64}`}
                      alt="Infographic for this answer"
                    />
                  </button>
                )
              )}
            </div>
          )}

          <div className="qa-chips">
            {(answer.chips ?? []).map((c) => (
              <span key={c} className="ent-pill">
                {c}
              </span>
            ))}
            <button type="button" className="qa-dismiss" onClick={clearAnswer} aria-label="Dismiss">
              <Close size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Enlarged infographic overlay (click the thumbnail to open; click / X to close). */}
      {igExpanded && answerIg && (
        <div className="ig-lightbox" onClick={() => setIgExpanded(false)}>
          <button
            type="button"
            className="ig-lightbox-close"
            onClick={() => setIgExpanded(false)}
            aria-label="Close"
          >
            <Close size={20} />
          </button>
          <img
            src={`data:${answerIg.mimeType};base64,${answerIg.imageBase64}`}
            alt="Infographic for this answer"
            className="ig-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {mode === "note" && notes.length > 0 && (
        <ul className="quiet-notes">
          {notes.map((n, i) => (
            <li key={`${n.t}-${i}`}>
              <span className="mono qn-time">{n.t}</span>
              <span>{n.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
